import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Shuffle, Download, Grid2X2, Grid, ImageIcon, Video, Square } from 'lucide-react'

const CheckeredPatternMaker = () => {
  const [colors] = useState([
    '#F26025', '#4F2B1B', '#C5DBF2', '#2628A5', '#EFA393', '#FEE01C', '#3C441D'
  ]);
  const [useTwoColors, setUseTwoColors] = useState(false);
  const [pattern, setPattern] = useState([]);
  const [rowHeights, setRowHeights] = useState([]);
  const [showLogo, setShowLogo] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [originalPattern, setOriginalPattern] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const canvasRef = useRef(null);
  const logoRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);

  const grainDataUrl = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 100;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(100, 100);
    for (let i = 0; i < imageData.data.length; i += 4) {
      const value = Math.random() * 255;
      imageData.data[i] = value;
      imageData.data[i+1] = value;
      imageData.data[i+2] = value;
      imageData.data[i+3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }, []);

  // Interpolate between two hex colors
  const interpolateColor = useCallback((color1, color2, t) => {
    // Convert hex to RGB
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);
    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);
    
    // Interpolate
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }, []);

  // Animate between brand colors - smoothly transitions from original to next brand color and back
  const animateColor = useCallback((originalHex, progress) => {
    // Find the closest brand color to the original
    let closestColorIndex = 0;
    let minDistance = Infinity;
    
    colors.forEach((brandColor, index) => {
      // Simple color distance calculation
      const r1 = parseInt(originalHex.slice(1, 3), 16);
      const g1 = parseInt(originalHex.slice(3, 5), 16);
      const b1 = parseInt(originalHex.slice(5, 7), 16);
      const r2 = parseInt(brandColor.slice(1, 3), 16);
      const g2 = parseInt(brandColor.slice(3, 5), 16);
      const b2 = parseInt(brandColor.slice(5, 7), 16);
      
      const distance = Math.sqrt(
        Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2)
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        closestColorIndex = index;
      }
    });
    
    // Get the next brand color in the cycle
    const nextColorIndex = (closestColorIndex + 1) % colors.length;
    const nextBrandColor = colors[nextColorIndex];
    
    // Use sine wave to create smooth transition: 0 -> 1 -> 0
    // At progress 0: original color (t = 0)
    // At progress 0.5: next brand color (t = 1)
    // At progress 1: back to original color (t = 0)
    const wave = Math.sin(progress * Math.PI);
    const t = wave; // t goes from 0 to 1 and back to 0
    
    // Interpolate between original and next brand color
    return interpolateColor(originalHex, nextBrandColor, t);
  }, [colors, interpolateColor]);

  const generateGradient = useCallback((colorA, colorB) => {
    return `linear-gradient(to bottom, ${colorA}, ${colorB})`;
  }, []);

  const getRandomFourColors = useCallback(() => {
    const shuffled = [...colors].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  }, [colors]);

  const generatePattern = useCallback(() => {
    const newPattern = [];
    const newRowHeights = [];
  
    // Randomly decide if we want a "small" or "large" grid
    const isSmallGrid = Math.random() < 0.5; // 50% chance for each grid size
  
    // Define row and column counts based on grid size choice
    const rows = isSmallGrid ? Math.floor(Math.random() * 4) + 3 : Math.floor(Math.random() * 6) + 10;
    const cols = isSmallGrid ? Math.floor(Math.random() * 4) + 3 : Math.floor(Math.random() * 6) + 10;
  
    // Use the first two colors in two-color mode; otherwise, use a random four-color set
    const colorSet = useTwoColors ? [...colors].sort(() => 0.5 - Math.random()).slice(0, 2) : getRandomFourColors();
  
    for (let i = 0; i < rows; i++) {
      const row = [];
      for (let j = 0; j < cols; j++) {
        let cellColor;
  
        if (useTwoColors) {
          // Checker pattern without gradient in two-color mode
          cellColor = colorSet[(i + j) % 2];
        } else {
          // Four-color mode with consistent gradient direction
          const isOddCell = (i + j) % 2 === 0;
          const baseColor = colorSet[isOddCell ? 0 : 2];   // Alternate base color
          const gradientColor = colorSet[isOddCell ? 1 : 3]; // Alternate gradient color
          cellColor = generateGradient(baseColor, gradientColor);
        }
  
        row.push(cellColor);
      }
      newPattern.push(row);
      newRowHeights.push(Math.floor(Math.random() * 2) + 1);
    }
    setPattern(newPattern);
    setRowHeights(newRowHeights);
    setOriginalPattern(newPattern);
  }, [colors, useTwoColors, generateGradient, getRandomFourColors]);
  

  useEffect(() => {
    generatePattern();
  }, [generatePattern]);

  // Animation loop - smooth brightness/saturation animation keeping brand colors
  useEffect(() => {
    if (!isAnimating) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      // Reset pattern to original when animation stops (brand colors)
      if (originalPattern.length > 0) {
        setPattern(originalPattern);
        setAnimationProgress(0);
      }
      return;
    }

    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      // 8 second cycle for slower, smoother animation
      const cycleDuration = 8000;
      const progress = (elapsed % cycleDuration) / cycleDuration;
      setAnimationProgress(progress);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isAnimating, originalPattern]);

  // Apply animation to pattern while keeping brand colors (hue unchanged)
  useEffect(() => {
    if (!isAnimating || originalPattern.length === 0) {
      return;
    }

    const animatedPattern = originalPattern.map(row =>
      row.map(cell => {
        if (useTwoColors) {
          // Solid color - animate brightness/saturation, keep hue
          return animateColor(cell, animationProgress);
        } else {
          // Gradient - extract colors, animate both, recreate gradient
          const colors = cell.match(/#[a-fA-F0-9]{6}/g);
          if (colors && colors.length >= 2) {
            const animatedColorA = animateColor(colors[0], animationProgress);
            const animatedColorB = animateColor(colors[1], animationProgress);
            return generateGradient(animatedColorA, animatedColorB);
          }
          return cell;
        }
      })
    );

    setPattern(animatedPattern);
  }, [animationProgress, isAnimating, originalPattern, useTwoColors, animateColor, generateGradient]);

  const handleDownload = () => {
    if (isAnimating) {
      downloadVideo();
    } else {
      downloadPNG();
    }
  };

  const downloadPNG = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const patternContainer = document.querySelector('.pattern-container');
    const containerWidth = patternContainer.clientWidth;
    const containerHeight = patternContainer.clientHeight;
    const aspectRatio = containerWidth / containerHeight;
    
    // Bestimme die Basis-Exportgröße (z.B. 3000px Breite)
    const baseExportWidth = 2000;
    // Berechne die Höhe basierend auf dem Seitenverhältnis
    const exportHeight = Math.round(baseExportWidth / aspectRatio);
    
    canvas.width = baseExportWidth;
    canvas.height = exportHeight;
    
    // Clear the canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Create grain pattern canvas with higher resolution
    const grainCanvas = document.createElement('canvas');
    grainCanvas.width = grainCanvas.height = 256;
    const grainCtx = grainCanvas.getContext('2d');
    const grainImageData = grainCtx.createImageData(256, 256);
    for (let i = 0; i < grainImageData.data.length; i += 4) {
      const value = Math.random() * 255;
      grainImageData.data[i] = value;
      grainImageData.data[i+1] = value;
      grainImageData.data[i+2] = value;
      grainImageData.data[i+3] = 255;
    }
    grainCtx.putImageData(grainImageData, 0, 0);

    const totalHeightUnits = rowHeights.reduce((sum, height) => sum + height, 0);
    let yOffset = 0;

    // Draw pattern
    for (let i = 0; i < pattern.length; i++) {
      const rowHeight = (rowHeights[i] / totalHeightUnits) * canvas.height;
      const cellWidth = canvas.width / pattern[i].length;

      for (let j = 0; j < pattern[i].length; j++) {
        const cell = pattern[i][j];
        const x = j * cellWidth;
        const y = yOffset;

        if (!useTwoColors) {
          // Handle gradient cells
          const gradient = ctx.createLinearGradient(x, y, x, y + rowHeight);
          const colors = cell.match(/#[a-fA-F0-9]{6}/g);
          if (colors && colors.length >= 2) {
            gradient.addColorStop(0, colors[0]);
            gradient.addColorStop(1, colors[1]);
            ctx.fillStyle = gradient;
          }
        } else {
          // Handle solid color cells
          ctx.fillStyle = cell;
        }

        // Draw the base cell
        ctx.fillRect(x, y, cellWidth, rowHeight);

        // Add grain effect for gradient mode
        if (!useTwoColors) {
          ctx.save();
          ctx.globalAlpha = 0.1;
          ctx.globalCompositeOperation = 'overlay';
          const pattern = ctx.createPattern(grainCanvas, 'repeat');
          ctx.fillStyle = pattern;
          ctx.fillRect(x, y, cellWidth, rowHeight);
          ctx.restore();
        }
      }
      yOffset += rowHeight;
    }

    // Draw logo if enabled
    if (showLogo && logoRef.current) {
      const logo = logoRef.current;
      const isMobile = window.innerWidth < 768;
      
      // Berechne Logo-Größe basierend auf den Export-Dimensionen
      const logoWidth = baseExportWidth * (isMobile ? 0.43 : 0.3);
      const logoHeight = logoWidth * (logo.height / logo.width);
      
      // Positioniere Logo
      const logoX = (baseExportWidth - logoWidth) / 2;
      const logoY = isMobile 
        ? exportHeight * 0.1 
        : (exportHeight - logoHeight) / 2;

      ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
    }

    // Create download link
    const link = document.createElement('a');
    link.download = 'chezfritz-mustervn.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const downloadVideo = async () => {
    setIsRecording(true);
    const patternContainer = document.querySelector('.pattern-container');
    const containerWidth = patternContainer.clientWidth;
    const containerHeight = patternContainer.clientHeight;
    const aspectRatio = containerWidth / containerHeight;
    const baseExportWidth = 1920;
    const exportHeight = Math.round(baseExportWidth / aspectRatio);

    // Create canvas for video recording
    const videoCanvas = document.createElement('canvas');
    videoCanvas.width = baseExportWidth;
    videoCanvas.height = exportHeight;
    const videoCtx = videoCanvas.getContext('2d');

    // Create stream from canvas
    const stream = videoCanvas.captureStream(30); // 30 FPS
    // Try MP4 codecs first, fallback to WebM if not supported
    let mimeType = 'video/mp4;codecs=h264';
    let options = {};
    
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/mp4';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp9';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }
    }
    
    // Set high quality options for better compression
    // For 1920x1080 at 30fps, use high bitrate (20-30 Mbps for high quality)
    if (mimeType.includes('mp4')) {
      options = {
        mimeType: mimeType,
        videoBitsPerSecond: 25000000, // 25 Mbps for high quality
      };
    } else if (mimeType.includes('vp9')) {
      options = {
        mimeType: mimeType,
        videoBitsPerSecond: 20000000, // 20 Mbps for VP9
      };
    } else {
      options = {
        mimeType: mimeType,
        videoBitsPerSecond: 15000000, // 15 Mbps for fallback
      };
    }
    
    const mediaRecorder = new MediaRecorder(stream, options);

    videoChunksRef.current = [];
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        videoChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(videoChunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      // Use .mp4 extension if MP4 format, otherwise .webm
      const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
      link.download = `pattern-animation.${extension}`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      setIsRecording(false);
    };

    // Start recording
    mediaRecorder.start();

    // Record for exactly one animation loop (8 seconds)
    const recordDuration = 8000;
    const startTime = performance.now();
    const fps = 30;
    const frameTime = 1000 / fps;
    let frameCount = 0;

    // Use original pattern and calculate hue rotation per frame
    const basePattern = originalPattern.length > 0 ? originalPattern : pattern;

    const drawFrame = (currentTime) => {
      const elapsed = currentTime - startTime;
      
      if (elapsed >= recordDuration) {
        mediaRecorder.stop();
        return;
      }

      // Calculate animation progress (8 second cycle, keeping brand colors)
      // For exactly one loop, progress goes from 0 to 1 over 8 seconds
      const cycleDuration = 8000;
      const currentProgress = Math.min(elapsed / cycleDuration, 1);

      // Clear canvas
      videoCtx.fillStyle = 'white';
      videoCtx.fillRect(0, 0, videoCanvas.width, videoCanvas.height);

      // Create grain pattern (reuse same grain for consistency)
      const grainCanvas = document.createElement('canvas');
      grainCanvas.width = grainCanvas.height = 256;
      const grainCtx = grainCanvas.getContext('2d');
      const grainImageData = grainCtx.createImageData(256, 256);
      for (let i = 0; i < grainImageData.data.length; i += 4) {
        const value = Math.random() * 255;
        grainImageData.data[i] = value;
        grainImageData.data[i + 1] = value;
        grainImageData.data[i + 2] = value;
        grainImageData.data[i + 3] = 255;
      }
      grainCtx.putImageData(grainImageData, 0, 0);

      const totalHeightUnits = rowHeights.reduce((sum, height) => sum + height, 0);
      let yOffset = 0;

      // Draw pattern with hue rotation applied
      for (let i = 0; i < basePattern.length; i++) {
        const rowHeight = (rowHeights[i] / totalHeightUnits) * videoCanvas.height;
        const cellWidth = videoCanvas.width / basePattern[i].length;

        for (let j = 0; j < basePattern[i].length; j++) {
          const cell = basePattern[i][j];
          const x = j * cellWidth;
          const y = yOffset;

          if (!useTwoColors) {
            // Extract colors from gradient and animate (keeping brand colors)
            const colors = cell.match(/#[a-fA-F0-9]{6}/g);
            if (colors && colors.length >= 2) {
              const animatedColorA = animateColor(colors[0], currentProgress);
              const animatedColorB = animateColor(colors[1], currentProgress);
              const gradient = videoCtx.createLinearGradient(x, y, x, y + rowHeight);
              gradient.addColorStop(0, animatedColorA);
              gradient.addColorStop(1, animatedColorB);
              videoCtx.fillStyle = gradient;
            }
          } else {
            // Solid color - animate (keeping brand colors)
            const animatedColor = animateColor(cell, currentProgress);
            videoCtx.fillStyle = animatedColor;
          }
          videoCtx.fillRect(x, y, cellWidth, rowHeight);

          if (!useTwoColors) {
            videoCtx.save();
            videoCtx.globalAlpha = 0.1;
            videoCtx.globalCompositeOperation = 'overlay';
            const pattern = videoCtx.createPattern(grainCanvas, 'repeat');
            videoCtx.fillStyle = pattern;
            videoCtx.fillRect(x, y, cellWidth, rowHeight);
            videoCtx.restore();
          }
        }
        yOffset += rowHeight;
      }

      // Draw logo if enabled
      if (showLogo && logoRef.current) {
        const logo = logoRef.current;
        const isMobile = window.innerWidth < 768;
        const logoWidth = baseExportWidth * (isMobile ? 0.43 : 0.3);
        const logoHeight = logoWidth * (logo.height / logo.width);
        const logoX = (baseExportWidth - logoWidth) / 2;
        const logoY = isMobile ? exportHeight * 0.1 : (exportHeight - logoHeight) / 2;
        videoCtx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
      }

      // Schedule next frame at target FPS
      frameCount++;
      const targetTime = startTime + (frameCount * frameTime);
      const delay = Math.max(0, targetTime - performance.now());
      
      if (delay > 0) {
        setTimeout(() => requestAnimationFrame(drawFrame), delay);
      } else {
        requestAnimationFrame(drawFrame);
      }
    };

    // Start drawing frames
    requestAnimationFrame(drawFrame);
  };


  return (
    <div className="h-screen w-screen flex flex-col relative">
      <div className="flex-grow grid pattern-container relative" style={{ 
        gridTemplateRows: rowHeights.map(height => `${height}fr`).join(' '),
        gridTemplateColumns: `repeat(${pattern[0]?.length || 1}, 1fr)`,
        border: '1px solid black',
        minHeight: '300px',
      }}>
        {pattern.length === 0 ? (
          <div>Loading pattern...</div>
        ) : (
          pattern.flat().map((color, index) => (
            <div key={index} style={{ 
              background: color,
              position: 'relative',
              overflow: 'hidden',
            }}>
              {!useTwoColors && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `url("${grainDataUrl}")`,
                  opacity: 0.05,  // grain visibility in preview
                  mixBlendMode: 'overlay',
                }} />
              )}
            </div>
            
          ))
        )}
          {showLogo && (
         <img 
         ref={logoRef}
         src="/logo.svg"
         alt="Logo"
         className="absolute w-[43%] left-1/2 transform -translate-x-1/2 z-10 mt-[15%]
                    md:w-1/3 md:mt-0 md:top-1/2 md:-translate-y-1/2"
       />
        )}
      </div>
      
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white/20 backdrop-blur-md border border-white/30 text-white p-2 rounded-full flex justify-between items-center shadow-lg">
        <button
          onClick={() => setShowLogo(!showLogo)}
          className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-bold py-2 px-4 rounded-full mx-1 transition-all"
        >
          <ImageIcon size={24} />
        </button>
        
        <button
          onClick={() => setUseTwoColors(!useTwoColors)}
          className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-bold py-2 px-4 rounded-full mx-1 transition-all"
        >
          {useTwoColors ? <Grid2X2 size={24} /> : <Grid size={24} />}
        </button>
        
        <button
          onClick={generatePattern}
          className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-bold py-2 px-4 rounded-full mx-1 transition-all"
        >
          <Shuffle size={24} />
        </button>
        
        <button
          onClick={() => setIsAnimating(!isAnimating)}
          className={`${isAnimating ? 'bg-white/30 hover:bg-white/40' : 'bg-white/10 hover:bg-white/20'} backdrop-blur-sm border border-white/20 text-white font-bold py-2 px-4 rounded-full mx-1 transition-all`}
        >
          {isAnimating ? <Square size={24} /> : <Video size={24} />}
        </button>
        
        <button
          onClick={handleDownload}
          disabled={isRecording}
          className={`${isRecording ? 'bg-white/5 cursor-not-allowed opacity-50' : 'bg-white/10 hover:bg-white/20'} backdrop-blur-sm border border-white/20 text-white font-bold py-2 px-4 rounded-full mx-1 transition-all`}
        >
          <Download size={24} />
        </button>
      </div>
      
      {isRecording && (
        <div className="fixed bottom-4 right-4 bg-white/20 backdrop-blur-md border border-white/30 text-white px-6 py-3 rounded-full shadow-lg z-50">
          Video being downloaded...
        </div>
      )}
      
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default CheckeredPatternMaker;